import { Avatar, Typography } from "@mui/material";
import { hasAnyAccessToResource } from "api/general";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { OLBackIcon } from "components/CustomIcons/OLBackIcon";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { ReactComponent as Globe01Icon } from "components/Icons/globe-01.svg";
import { ReactComponent as LogOut01Icon } from "components/Icons/log-out-01.svg";
import { ReactComponent as Settings01Icon } from "components/Icons/settings-01.svg";
import { tabLabels } from "components/Layout/GlobalManagement/Settings/Settings";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useAuth } from "hooks/auth";
import { useMessage } from "hooks/intl";
import { useIsOverflown } from "hooks/useIsOverflown";
import { kebabCase } from "lodash";
import React, { useEffect, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import { Link, useLocation } from "react-router-dom";
import {
  selectEmail,
  selectFirstName,
  selectLastName,
} from "reduxState/adminData/adminDataSlice";
import {
  selectCustomLanguageData,
  setCustomLanguageData,
} from "reduxState/customLanguageData/customLanguageDataSlice";
import { selectInformationsHeight } from "reduxState/informations/informationsSlice";
import { selectIsSidebarOpen } from "reduxState/sidebar/sidebarSlice";
import {
  selectAvaiableTranslations,
  selectDefaultLanguageData,
} from "reduxState/translations/translationsSlice";
import { getEnvVariable } from "utilities/env";
import { links } from "utilities/links";
import { getAcronym } from "utilities/strings";
import { EnvironmentBadge } from "./EnvironmentBadge/EnvironmentBadge";
import { useStyles } from "./TopHeader.styles";
import { WhatsNewDrawer } from "./WhatsNewDrawer";

export type Breadcrumbs =
  | {
      parent: {
        label: string;
        path: string;
        state?: {
          [key: string]: any;
        };
      };
    }
  | undefined;

interface Props {
  title: string;
  clearLink?: string;
  breadcrumbs?: Breadcrumbs;
  badge?: React.ReactNode;
  "data-hj-suppress"?: boolean;
}

const profileItems = {
  editProfile: "editProfile",
  changePassword: "changePassword",
  logout: "logout",
} as const;

export const TopHeader = ({
  title,
  clearLink,
  breadcrumbs,
  badge,
  "data-hj-suppress": dataHjSuppress,
}: Props) => {
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const firstName = useAppSelector(selectFirstName);
  const lastName = useAppSelector(selectLastName);
  const email = useAppSelector(selectEmail);
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const hasAnyRolePermission = hasAnyAccessToResource("ACL");
  const hasAnyAdminPermission = hasAnyAccessToResource("ADMIN");
  const translations = useAppSelector(selectAvaiableTranslations);
  const informationsHeight = useAppSelector(selectInformationsHeight);
  const classes = useStyles({
    isSidebarOpen,
    informationsHeight: informationsHeight,
  });
  const { message } = useMessage();
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const { observeElement, isOverflown } = useIsOverflown();
  const { logout } = useAuth();

  useEffect(() => {
    if (headerRef.current) {
      const headerHeight = headerRef.current.offsetHeight;
      const elementUnderHeader = headerRef.current
        .nextElementSibling as HTMLElement | null;

      if (elementUnderHeader) {
        // Check if an element under header already has marginTop
        const computedStyle = window.getComputedStyle(elementUnderHeader);
        const existingMarginTop = parseInt(computedStyle.marginTop, 10) || 0;

        // Set marginTop as a sum of header's height and existing margin top
        elementUnderHeader.style.marginTop = `${headerHeight + existingMarginTop}px`;
      }

      return () => {
        if (elementUnderHeader) {
          elementUnderHeader.style.marginTop = "";
        }
      };
    }
  }, []);

  const breadcrumbsValue = (
    <>
      <Link
        to={breadcrumbs?.parent.path as string}
        className={classes.breadcrumbLink}
        state={breadcrumbs?.parent.state}
        data-hj-suppress={dataHjSuppress}
      >
        {breadcrumbs?.parent.label}
      </Link>{" "}
      /
    </>
  );

  const userIdentifier =
    firstName && lastName ? `${firstName} ${lastName}` : email;

  const settings = [
    {
      label: tabLabels.roles,
      isShown: hasAnyRolePermission,
    },
    {
      label: tabLabels.admins,
      isShown: hasAnyAdminPermission,
    },
    { label: tabLabels.tenants, isShown: true },
    { label: tabLabels.translations, isShown: true },
  ];

  const settingsFilteredByPermission = settings
    .filter((item) => item.isShown)
    .map((item) => ({
      label: message({ id: item.label }),
      itemDataTestId: `${item.label}-tab`,
      link: {
        pathname: links.settings,
        hash: item.label,
        replace: true,
      },
    }));

  const profile = [
    {
      label: profileItems.editProfile,
      isShown: hasAnyAdminPermission,
      link: {
        pathname: links.adminData,
      },
    },
    {
      label: profileItems.changePassword,
      isShown: true,
      link: {
        pathname: links.changePassword,
      },
    },
    {
      label: profileItems.logout,
      icon: <LogOut01Icon />,
      customClass: classes.logout,
      isShown: true,
      onClick: logout,
    },
  ];

  const profileFilteredByPermission = profile
    .filter((item) => item.isShown)
    .map((item) => ({
      label: message({ id: item.label }),
      onClick: item.onClick,
      icon: item.icon,
      customClass: item.customClass,
      link: item.link,
    }));
  const customLanguage = useAppSelector(selectCustomLanguageData)?.code || "";

  // Gitbook env's
  const gitbookToken = getEnvVariable("REACT_APP_GITBOOK_TOKEN");
  const gitbookSpaceId = getEnvVariable("REACT_APP_GITBOOK_SPACE_ID");

  return (
    <header className={classes.header} ref={headerRef}>
      <div
        className={`${classes.flexWrapper} ${classes.gapLarge} ${classes.hiddenOverflow}`}
      >
        {clearLink && (
          <OLBackIcon
            link={state?.prevPath || clearLink}
            className={classes.backIcon}
            data-test-id="header-back-button"
          />
        )}
        <Typography
          className={classes.title}
          ref={observeElement}
          data-test-id={`${kebabCase(title)}-header`}
        >
          {title ? (
            <>
              {breadcrumbs && breadcrumbsValue}{" "}
              <Tooltip
                text={title}
                disableHoverListener={!isOverflown}
                placement="bottom-start"
              >
                <span data-hj-suppress={dataHjSuppress}>{title}</span>
              </Tooltip>
            </>
          ) : (
            <Skeleton width={100} />
          )}
        </Typography>
        {badge && badge}
      </div>
      <div
        className={`${classes.headerButtonsWrapper} ${classes.gapLarge} ${classes.mediaContainer}`}
      >
        <OLMenuButton
          className={`${classes.flexWrapper} ${classes.item}`}
          startIcon={<Globe01Icon className={classes.icon} />}
          endIcon={<ChevronDownIcon />}
          data-test-id="language"
          items={translations.map((item) => ({
            label: message({ id: item.name }),
            onClick: () => {
              dispatch(setCustomLanguageData({ code: item.code }));
            },
          }))}
          submit={
            translations ? (
              translations.find((item) => item.code === customLanguage)?.name ||
              defaultLang.name
            ) : (
              <Skeleton width={100} />
            )
          }
        />
        <OLMenuButton
          className={classes.item}
          data-sentry-unmask
          data-test-id="top-nav-settings-item"
          hideSearch
          items={settingsFilteredByPermission}
          startIcon={<Settings01Icon className={classes.icon} />}
          submit={message({ id: "settings" })}
        />
        {gitbookToken && gitbookSpaceId ? <WhatsNewDrawer /> : null}
        <OLMenuButton
          className={`${classes.flexWrapper} ${classes.item}`}
          endIcon={<ChevronDownIcon />}
          hideSearch
          items={[
            {
              label: message({ id: "userGuide" }),
              link: {
                pathname: "https://help.openloyalty.io/",
                isExternal: true,
              },
            },
            {
              label: message({ id: "apiDocumentation" }),
              link: {
                pathname: "https://apidocs.openloyalty.io/",
                isExternal: true,
              },
            },
          ]}
          submit={message({ id: "more" })}
        />
        <div className={classes.verticalSeparator}>
          <div className={classes.separator} />
        </div>
        <EnvironmentBadge />
        <OLMenuButton
          items={profileFilteredByPermission}
          submit={userIdentifier}
          startIcon={
            <Avatar
              alt={userIdentifier}
              className={classes.avatar}
              variant="circular"
              data-hj-suppress
            >
              {getAcronym(userIdentifier)}
            </Avatar>
          }
          hideSearch
          className={`${classes.flexWrapper} ${classes.item}`}
          data-test-id="side-nav-profile-item"
        />
      </div>
    </header>
  );
};
